class VerticalSliderComponent extends HTMLElement {
  #trackElement;
  #previousButtonElement;
  #nextButtonElement;
  #slideElements = [];
  #slideScrollPositions = [];
  #currentIndex = -1;
  #maxScrollTop = 0;
  #resizeObserver;
  #backgroundElement;
  #modalSliderElement;
  #modalSliderWrapperElement;
  #modalSliderCloseElement;
  #isModalSliderBound = false;
  #backgroundSlideElement;
  #lockedScrollTop = 0;

  constructor() {
    super();

    this.#trackElement = this.querySelector('[data-slider-track]');
    this.#previousButtonElement = this.querySelector('[data-slider-previous]');
    this.#nextButtonElement = this.querySelector('[data-slider-next]');
  }

  connectedCallback() {
    if (!this.#trackElement) return;

    this.#slideElements = Array.from(this.#trackElement.querySelectorAll('[data-slider-slide]'));
    if (!this.#slideElements.length) return;

    const sectionElement = this.closest('section');
    this.#backgroundElement = sectionElement?.querySelector('[data-background]');
    this.#modalSliderElement = sectionElement?.querySelector('[data-modal-slider]');
    this.#modalSliderWrapperElement = sectionElement?.querySelector('[data-modal-slider-wrapper]');
    this.#modalSliderCloseElement = sectionElement?.querySelector('[data-modal-slider-close]');

    this.addEventListener('vertical-slide:toggle', this.onSlideToggle);
    this.#modalSliderCloseElement?.addEventListener('click', this.onModalSliderCloseClick);
    this.#previousButtonElement?.addEventListener('click', this.onPreviousButtonClick);
    this.#nextButtonElement?.addEventListener('click', this.onNextButtonClick);
    this.#trackElement.addEventListener('scroll', this.onScroll, { passive: true });
    this.#trackElement.addEventListener('keydown', this.onKeydown);
    this.#trackElement.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.#trackElement.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.#trackElement.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

    // slide height changes with its content (the accordion opens inside it), so the
    // cached offsets have to be rebuilt whenever a slide or the track resizes
    this.#resizeObserver = new ResizeObserver(() => this.measureSlides());
    this.#resizeObserver.observe(this.#trackElement);
    this.#slideElements.forEach((slideElement) => this.#resizeObserver.observe(slideElement));

    this.measureSlides();

    console.log("this.isMobile()", this.isMobile());
    if (this.isMobile()) {
      setTimeout(()=> this.goToSlide(3), 0)
    } else if (this.isTrackOverflowing()) {
      this.goToNextSlide();
    }
  }

  disconnectedCallback() {
    this.#trackElement?.removeEventListener('scroll', this.onScroll);
    this.#trackElement?.removeEventListener('keydown', this.onKeydown);
    this.#trackElement?.removeEventListener('touchstart', this.onTouchStart);
    this.#trackElement?.removeEventListener('touchend', this.onTouchEnd);
    this.#trackElement?.removeEventListener('touchcancel', this.onTouchEnd);
    this.removeEventListener('vertical-slide:toggle', this.onSlideToggle);
    this.#modalSliderCloseElement?.removeEventListener('click', this.onModalSliderCloseClick);
    this.#modalSliderElement?.swiper?.off('slideChange', this.onModalSlideChange);
    this.#previousButtonElement?.removeEventListener('click', this.onPreviousButtonClick);
    this.#nextButtonElement?.removeEventListener('click', this.onNextButtonClick);
    this.#resizeObserver?.disconnect();
  }

  get slides() {
    return this.#slideElements;
  }

  get currentIndex() {
    return this.#currentIndex;
  }

  // on mobile the first slide rests against the bottom edge of the track (`scroll-snap-align: end`),
  // so the list gets a leading spacer filling everything above it — sized so that the first slide's
  // resting position is scrollTop 0, otherwise the track can never sit at its own start
  measureLeadSpace() {
    if (!this.isMobile()) return null;

    const trackStyles = getComputedStyle(this.#trackElement);
    const leadSpace =
      this.#trackElement.clientHeight -
      (parseFloat(trackStyles.paddingTop) || 0) -
      (parseFloat(trackStyles.scrollPaddingBottom) || 0) -
      this.#slideElements[0].offsetHeight;

    return Math.max(0, leadSpace);
  }

  goToSlide(index) {
    const targetIndex = Math.max(0, Math.min(index, this.#slideElements.length - 1));
    if (this.#slideScrollPositions[targetIndex] === undefined) return;

    this.#trackElement.scrollTo({
      top: this.#slideScrollPositions[targetIndex],
      behavior: 'smooth',
    });
  }

  goToNextSlide() {
    const nextIndex = this.#slideScrollPositions.findIndex(
      (scrollPosition) => scrollPosition > this.#trackElement.scrollTop + 1
    );
    if (nextIndex !== -1) this.goToSlide(nextIndex);
  }

  goToPreviousSlide() {
    for (let index = this.#slideScrollPositions.length - 1; index >= 0; index--) {
      if (this.#slideScrollPositions[index] < this.#trackElement.scrollTop - 1) {
        this.goToSlide(index);
        return;
      }
    }
  }

  // where a slide comes to rest depends on the breakpoint: it snaps to the middle of the track on
  // desktop (`scroll-snap-align: center`) and to its padded bottom edge on mobile
  // (`scroll-snap-align: end`). Storing that resting scrollTop keeps every comparison against
  // scrollTop honest. Subtracting scrollTop makes the measurement scroll-independent, so it only
  // changes on resize.
  measureSlides() {
    const leadSpace = this.measureLeadSpace();

    const trackStyles = getComputedStyle(this.#trackElement);
    const scrollPaddingBottom = parseFloat(trackStyles.scrollPaddingBottom) || 0;
    const trackTop = this.#trackElement.getBoundingClientRect().top - this.#trackElement.scrollTop;
    const trackHeight = this.#trackElement.clientHeight;
    const scrollHeight = this.#trackElement.scrollHeight;
    const isMobileLayout = this.isMobile();
    const slideRects = this.#slideElements.map((slideElement) => slideElement.getBoundingClientRect());

    this.#maxScrollTop = scrollHeight - trackHeight;
    this.#slideScrollPositions = slideRects.map((slideRect) => {
      const restingScrollTop = isMobileLayout
        ? slideRect.bottom - trackTop - trackHeight + scrollPaddingBottom
        : slideRect.top - trackTop - (trackHeight - slideRect.height) / 2;

      return Math.min(Math.max(restingScrollTop, 0), this.#maxScrollTop);
    });

    if (leadSpace === null) {
      this.#trackElement.style.removeProperty('--lead-space');
    } else {
      this.#trackElement.style.setProperty('--lead-space', `${leadSpace}px`);
    }

    this.syncNavigation();
  }

  syncNavigation() {
    if (!this.#slideElements.length) return;

    const scrollTop = this.#trackElement.scrollTop;
    const activeIndex = this.getActiveSlideIndex(scrollTop);

    if(!this.#previousButtonElement.disabled || !this.#nextButtonElement.disabled) {
      if (this.#previousButtonElement) this.#previousButtonElement.disabled = scrollTop <= 1;
      if (this.#nextButtonElement) this.#nextButtonElement.disabled = scrollTop >= this.#maxScrollTop - 1;
    }

    if (activeIndex === this.#currentIndex) return;

    this.#currentIndex = activeIndex;

    document.dispatchEvent(
      new CustomEvent('vertical-slider:slide-changed', {
        detail: {
          index: activeIndex,
          slide: this.#slideElements[activeIndex],
          slider: this,
        },
      })
    );
  }

  getActiveSlideIndex(scrollTop = this.#trackElement.scrollTop) {
    if (scrollTop >= this.#maxScrollTop - 1) return this.#slideElements.length - 1;

    return this.#slideScrollPositions.reduce(
      (closestIndex, scrollPosition, index) =>
        Math.abs(scrollPosition - scrollTop) <
        Math.abs(this.#slideScrollPositions[closestIndex] - scrollTop)
          ? index
          : closestIndex,
      0
    );
  }

  // the slides only need scrolling when they are taller than the track that holds them —
  // a set short enough to fit stays where it is instead of being pushed to the next slide
  isTrackOverflowing() {
    return this.#maxScrollTop > 1;
  }

  // the opened accordion owns the background: its slide carries the picture in a template,
  // and with every accordion closed the section video shows through again
  renderBackgroundImage(slideElement) {
    if (!this.#backgroundElement || slideElement === this.#backgroundSlideElement) return;

    this.#backgroundSlideElement = slideElement;

    const imageTemplateElement = slideElement?.querySelector('[data-slide-image]');
    const pictureElement = imageTemplateElement?.content.firstElementChild?.cloneNode(true);
    const imageElement = pictureElement?.querySelector('img');

    if (!imageElement) {
      this.removeBackgroundImage();
      return;
    }

    pictureElement.setAttribute('data-background-image', '');

    imageElement.loading = 'eager';

    imageElement.onload = () => {
      if (this.#backgroundSlideElement !== slideElement) return;

      this.removeBackgroundImage();
      this.#backgroundElement.append(pictureElement);
    };

    imageElement.onerror = () => {
      console.error(`Failed to load background image: ${imageElement.currentSrc || imageElement.src}`);
    };

    // template content lives in an inert document, so the image only loads once it is adopted
    document.adoptNode(pictureElement);
  }

  removeBackgroundImage() {
    this.#backgroundElement.querySelector('[data-background-image]')?.remove();
  }

  // the open accordion is the single source of truth for both the background and the overlay
  // slider, so every change to it — a heading click or the overlay close button — resyncs them
  syncOpenSlide() {
    const openAccordionElement = this.querySelector('accordion-toggle-item[open]');
    const openSlideElement = openAccordionElement?.closest('[data-slider-slide]');

    this.renderBackgroundImage(openSlideElement);
    this.toggleModalSlider(openSlideElement);
  }

  closeOpenSlide() {
    this.querySelector('accordion-toggle-item[open]')?.removeAttribute('open');
    this.syncOpenSlide();
  }

  // swiping the overlay makes another slide the open one — the accordion carries that state, so it
  // is moved first and the background follows from it like it does for a heading click
  openSlideByIndex(index) {
    const slideElement = this.#slideElements[index];
    if (!slideElement) return;

    slideElement.querySelector('accordion-toggle-item')?.setAttribute('open', '');
    this.syncOpenSlide();
  }

  // on mobile the accordion panel stays collapsed and its content lives in the overlay swiper
  // instead: opening a slide reveals that slider on the matching slide, closing hides it again
  toggleModalSlider(slideElement) {
    if (!this.#modalSliderWrapperElement) return;

    const isActive = this.isMobile() && Boolean(slideElement);
    this.#modalSliderWrapperElement.classList.toggle('vertical-modal-slider__wrapper--active', isActive);
    if (!isActive) return;

    this.bindModalSlider();
    this.#modalSliderElement?.swiper?.slideTo(this.#slideElements.indexOf(slideElement));
  }

  // the overlay swiper is built on intersection, so its instance can appear after this component
  // connects — the listener is attached on first use instead, once there is something to listen to
  bindModalSlider() {
    if (this.#isModalSliderBound || !this.#modalSliderElement?.swiper) return;

    this.#modalSliderElement.swiper.on('slideChange', this.onModalSlideChange);
    this.#isModalSliderBound = true;
  }

  // a drag starting in the lead space belongs to the page, not to the slider — and both scroll on
  // the same axis, so no `touch-action` value can separate them. Making the track unscrollable for
  // the length of the gesture hands the drag to the next scroll container up instead
  lockTrackScroll() {
    if (this.#trackElement.classList.contains('vertical-slider__list--drag-locked')) return;

    this.#lockedScrollTop = this.#trackElement.scrollTop;
    this.#trackElement.classList.add('vertical-slider__list--drag-locked');
    this.#trackElement.scrollTo({ top: this.#lockedScrollTop, behavior: 'instant' });
  }

  unlockTrackScroll() {
    if (!this.#trackElement.classList.contains('vertical-slider__list--drag-locked')) return;

    this.#trackElement.classList.remove('vertical-slider__list--drag-locked');
    this.#trackElement.scrollTo({ top: this.#lockedScrollTop, behavior: 'instant' });
  }

  onTouchStart = (event) => {
    if (event.target.closest('[data-slider-lead]')) this.lockTrackScroll();
  };

  onTouchEnd = () => this.unlockTrackScroll();

  onSlideToggle = () => this.syncOpenSlide();

  onModalSliderCloseClick = () => this.closeOpenSlide();

  onModalSlideChange = (swiperInstance) => this.openSlideByIndex(swiperInstance.activeIndex);

  onScroll = () => requestAnimationFrame(() => this.syncNavigation());

  onKeydown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.goToNextSlide();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.goToPreviousSlide();
    }
  };

  onPreviousButtonClick = () => this.goToPreviousSlide();

  onNextButtonClick = () => this.goToNextSlide();

  isMobile() {
    return !window.matchMedia("(min-width: 750px)").matches
  }
}

if (!customElements.get('vertical-slider-component')) {
  customElements.define('vertical-slider-component', VerticalSliderComponent);
}

class AccordionToggleItem extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  constructor() {
    super();
    this.onToggle = this.onToggle.bind(this);
    this.isSyncing = false;

    this.button = this.querySelector('[data-accordion-toggle]');
    this.panel = this.querySelector('.accordion__content-wrapper');
  }

  connectedCallback() {
    if (!this.button || !this.panel) return;
    this.button?.addEventListener('click', this.onToggle);
    this.setOpenState(this.hasAttribute('open'), false);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onToggle);
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    if (this.isSyncing || name !== 'open') return;
    this.setOpenState(this.hasAttribute('open'), false);
  }

  onToggle() {
    this.setOpenState(!this.hasAttribute('open'), true);

    this.dispatchEvent(new CustomEvent('vertical-slide:toggle', {
      bubbles: true
    }))
  }

  setOpenState(isOpen, syncAttribute) {
    if (!this.panel || !this.button) return;

    this.classList.toggle('is-open', isOpen);
    this.panel.setAttribute('aria-hidden', String(!isOpen));
    this.button.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      const parentSelector = this.getAttribute('data-parent-selector');
      if (parentSelector) {
        const parent = this.closest(parentSelector);
        if (parent) {
          parent.querySelectorAll('accordion-toggle-item[open]').forEach((item) => {
            if (item !== this) item.removeAttribute('open');
          });
        }
      }
    }

    if (!syncAttribute) return;

    this.isSyncing = true;
    this.toggleAttribute('open', isOpen);
    this.isSyncing = false;
  }
}

if (!customElements.get('accordion-toggle-item')) {
  customElements.define('accordion-toggle-item', AccordionToggleItem);
}
